/**
 * Root route table for the Ten-Teams SPA.
 *
 * - Public routes render outside AuthLayout (rules, fixtures, knockout, results, map, stats,
 *   support, list share view, login, etc.). Note: some Firestore paths those pages read
 *   still require a signed-in user (see ARCHITECTURE.md).
 * - Authenticated gameplay routes sit behind AuthLayout (profile, league, join, blog).
 * - Admin routes add AdminAuthLayout (requires claims.admin).
 * - Firebase OOB links (password reset, email verify, magic-link sign-in) redirect before routes render.
 * - /league/:id/progress is a legacy redirect to /league/:id#standings-chart (chart lives on LeaguePage).
 * - Lazy-loaded: map, knockout, league-progress redirect, admin, list, blog, join, reset-password,
 *   admin-analytics, stats, support (+ thanks / refund-policy).
 *
 * Full routing reference: ARCHITECTURE.md at repo root.
 */
import { useContext, useEffect, lazy, Suspense } from 'react';
import './App.css';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Center, Spinner } from '@chakra-ui/react';
import { isSignInWithEmailLink } from 'firebase/auth';
import { withSentryReactRouterV6Routing } from '@sentry/react';
import { FirebaseContext } from './session/FirebaseContext';
import { VIEWPORT_MIN_H } from '@/constants/mobileLayout';
import { buildAuthActionPath, parseAuthActionFromUrl } from './helpers/auth/authAction';
import { landingPathForUser } from './helpers/auth/landingRoutes';
import { logEvent } from 'firebase/analytics';
import { AuthLoadingScreen } from './components/AuthCards/auth-ui';
import AuthLayout from './views/AuthLayout';
import EmailVerificationPage from './views/EmailVerificationPage';
import FinishSignInPage from './views/FinishSignInPage';
import RulesPage from './views/RulesPage';
import ProfilePage from './views/ProfilePage';
import TeamsPage from './views/TeamsPage';
import AiLeaguePage from './views/AiLeaguePage';
import FixturesPage from './views/FixturesPage';
import ResultsPage from './views/ResultsPage';
import SignOutPage from './views/SignOutPage';
import LogInPage from './views/LogInPage';
import UnsubscribePage from './views/UnsubscribePage';
import LeaguePage from './views/LeaguePage';
import AdminAuthLayout from './views/AdminAuthLayout';

const JoinLeagueView = lazy(() => import('./views/JoinLeagueView'));
const AdminPage = lazy(() => import('./views/AdminPage'));
const ListPage = lazy(() => import('./views/ListPage'));
const BlogPage = lazy(() => import('./views/BlogPage'));
const ResetPasswordPage = lazy(() => import('./views/ResetPasswordPage'));
const AdminAnalyticsPage = lazy(() => import('./views/AdminAnalyticsPage'));
const StatsPage = lazy(() => import('./views/StatsPage'));
const MapPage = lazy(() => import('./views/MapPage'));
const KnockoutPage = lazy(() => import('./views/KnockoutPage'));
const LeagueProgressPage = lazy(() => import('./views/LeagueProgressPage'));
const SupportPage = lazy(() => import('./views/SupportPage'));
const SupportThanksPage = lazy(() => import('./views/SupportPage/ThanksPage'));
const SupportRefundPolicyPage = lazy(() => import('./views/SupportPage/RefundPolicyPage'));

const SentryRoutes = withSentryReactRouterV6Routing(Routes);

export default function App() {
  const ctx = useContext(FirebaseContext);
  const location = useLocation();

  useEffect(() => {
    logEvent(ctx.analytics, 'page_view', { page_title: location.pathname, page_path: location.pathname });
  }, [location, ctx.analytics]);

  const authAction = parseAuthActionFromUrl(window.location.href);
  const currentHref = window.location.href;

  if (authAction.mode === 'resetPassword' && authAction.oobCode && location.pathname !== '/reset-password') {
    return (
      <div className='App'>
        <Navigate replace to={buildAuthActionPath('/reset-password', currentHref)} />
      </div>
    );
  }

  if (authAction.mode === 'verifyEmail' && authAction.oobCode && location.pathname !== '/verifyEmail') {
    return (
      <div className='App'>
        <AuthLoadingScreen message='Verifying your email…' />
        <Navigate replace to={buildAuthActionPath('/verifyEmail', currentHref)} />
      </div>
    );
  }

  if (authAction.mode === 'signIn' && authAction.oobCode && location.pathname !== '/finish-sign-in') {
    return (
      <div className='App'>
        <AuthLoadingScreen message='Signing you in…' />
        <Navigate replace to={buildAuthActionPath('/finish-sign-in', currentHref)} />
      </div>
    );
  }

  if (
    isSignInWithEmailLink(ctx.auth, currentHref) &&
    location.pathname !== '/finish-sign-in'
  ) {
    return (
      <div className='App'>
        <AuthLoadingScreen message='Signing you in…' />
        <Navigate replace to={buildAuthActionPath('/finish-sign-in', currentHref)} />
      </div>
    );
  }

  return (
    <div className='App'>
      <SentryRoutes>
        <Route path='/home' element={<LandingRedirect />} />
        <Route index element={<LandingRedirect />} />
        <Route path='/teams' element={<TeamsPage />} />
        <Route path='/fixtures' element={<FixturesPage />} />
        <Route
          path='/knockout'
          element={
            <Suspense fallback={<Loading />}>
              <KnockoutPage />
            </Suspense>
          }
        />
        <Route path='/results' element={<ResultsPage />} />
        <Route path='/scores' element={<Navigate to='/fixtures' replace />} />
        <Route
          path='/map'
          element={
            <Suspense fallback={<Loading />}>
              <MapPage />
            </Suspense>
          }
        />
        <Route path='/rules' element={<RulesPage />} />
        <Route path='/ai-league' element={<AiLeaguePage />} />
        <Route
          path='/stats'
          element={
            <Suspense fallback={<Loading />}>
              <StatsPage />
            </Suspense>
          }
        />
        <Route
          path='/support'
          element={
            <Suspense fallback={<Loading />}>
              <SupportPage />
            </Suspense>
          }
        />
        <Route
          path='/support/thanks'
          element={
            <Suspense fallback={<Loading />}>
              <SupportThanksPage />
            </Suspense>
          }
        />
        <Route
          path='/support/refund-policy'
          element={
            <Suspense fallback={<Loading />}>
              <SupportRefundPolicyPage />
            </Suspense>
          }
        />
        <Route path='/unsubscribe/:userID' element={<UnsubscribePage />} />
        <Route path='/signout' element={<SignOutPage />} />
        <Route path='/login' element={<LogInPage />} />
        <Route path='/finish-sign-in' element={<FinishSignInPage />} />
        <Route
          path='/reset-password'
          element={
            <Suspense fallback={<Loading />}>
              <ResetPasswordPage />
            </Suspense>
          }
        />
        <Route
          path='/list'
          element={
            <Suspense fallback={<Loading />}>
              <ListPage />
            </Suspense>
          }
        />
        <Route path='/verifyEmail' element={<EmailVerificationPage />} />
        <Route element={<AuthLayout redirectTo='/login' />}>
          <Route
            path='/join/:authCode'
            element={
              <Suspense fallback={<Loading />}>
                <JoinLeagueView />
              </Suspense>
            }
          />
          <Route
            path='/blog'
            element={
              <Suspense fallback={<Loading />}>
                <BlogPage />
              </Suspense>
            }
          />
          <Route path='/profile' element={<ProfilePage />} />
          <Route path='/league/:id' element={<LeaguePage />} />
          <Route
            path='/league/:id/progress'
            element={
              <Suspense fallback={<Loading />}>
                <LeagueProgressPage />
              </Suspense>
            }
          />
          <Route element={<AdminAuthLayout />}>
            <Route
              path='/admin'
              element={
                <Suspense fallback={<Loading />}>
                  <AdminPage />
                </Suspense>
              }
            />
            <Route
              path='/admin-analytics'
              element={
                <Suspense fallback={<Loading />}>
                  <AdminAnalyticsPage />
                </Suspense>
              }
            />
            <Route path='/admin-stats' element={<Navigate replace to='/admin-analytics' />} />
          </Route>
        </Route>
        <Route path='*' element={<LandingRedirect />} />
      </SentryRoutes>
    </div>
  );
}

function LandingRedirect() {
  const ctx = useContext(FirebaseContext);

  if (!ctx.authReady) {
    return <Loading />;
  }

  return <Navigate replace to={landingPathForUser(Boolean(ctx.user))} />;
}

function Loading() {
  return (
    <Center w='full' minH={VIEWPORT_MIN_H}>
      <Spinner />
    </Center>
  );
}
